/**
 * `EngineSelector` — floating engine quick-switch control.
 *
 * Compact dropdown in the top-left of the map that lets the operator
 * switch the active rendering engine on a project. The selection is
 * persisted to `localStorage` (per project id when provided). The
 * actual engine swap is owned by the host shell (`TwinShell`) so this
 * component only emits the new value and renders the chrome.
 *
 * WP-11 design-system compliance sweep:
 *   - The previous `⚙` emoji glyph is replaced by `<Icon name="settings" />`
 *     (Material Symbols Rounded) per `DESIGN_POLICY.md` §5
 *     ("no emoji as product icons") and the masterplan §2 audit.
 *   - The raw `rgba(0, 0, 0, …)` shadow strings on the trigger button
 *     and the popover are replaced by `tokens.elevation[1]` /
 *     `tokens.elevation[3]` so the elevation hierarchy stays
 *     consistent with the BasemapSwitcher and the rest of the
 *     floating chrome.
 *   - The absolute pixel offsets (`left: 16`, `top: 16`,
 *     `minWidth: 280`, `1px solid …`) are replaced by `tokens.space.*`,
 *     `tokens.layout.popoverSm`, `tokens.border.thin`, and
 *     `tokens.border.width.thin`.
 *   - The 40 px touch-target floor is honoured on every interactive
 *     element via `tokens.density.touchMin`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { EngineId } from '@geosoft/viewer-sdk';
import { Icon, tokens } from '@/design';

export const ENGINE_OPTIONS: Array<{ id: EngineId; label: string; description: string }> = [
  { id: 'maplibre', label: 'MapLibre (2D)', description: 'Default 2D map, raster + vector overlays.' },
  {
    id: 'openlayers',
    label: 'Canvas 2D (no GPU)',
    description: 'OpenLayers canvas fallback for VMs without WebGL.',
  },
  { id: 'cesium', label: 'Cesium (3D)', description: '3D globe with terrain and 3D Tiles support.' },
  { id: 'potree', label: 'Potree (Point Cloud)', description: 'Massive point-cloud streaming.' },
  { id: 'giro3d', label: 'Giro3D (3D)', description: 'Three.js-based 3D scene.' },
  { id: 'three', label: 'Three.js (BIM)', description: 'BIM / engineering models.' },
  { id: 'deckgl', label: 'deck.gl (2D+)', description: 'High-performance vector overlays.' },
];

const STORAGE_KEY_PREFIX = 'geoviewer.engine';

export function loadPreferredEngine(scope: string): EngineId {
  if (typeof window === 'undefined') return 'maplibre';
  try {
    const v = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}.${scope}`);
    if (v && ENGINE_OPTIONS.some((o) => o.id === v)) return v as EngineId;
  } catch {
    /* ignore */
  }
  return 'maplibre';
}

export function savePreferredEngine(scope: string, engine: EngineId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}.${scope}`, engine);
  } catch {
    /* ignore */
  }
}

export interface EngineSelectorProps {
  engine: EngineId;
  onChange: (next: EngineId) => void;
}

export function EngineSelector({ engine, onChange }: EngineSelectorProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const activeOption = useMemo(
    () => ENGINE_OPTIONS.find((o) => o.id === engine),
    [engine],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const select = (id: EngineId) => {
    setOpen(false);
    if (id !== engine) onChange(id);
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: tokens.space[4],
        top: tokens.space[4],
        zIndex: tokens.zIndex.panel,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Rendering engine: ${activeOption?.label ?? engine}`}
        onClick={() => setOpen((v) => !v)}
        title={activeOption?.description}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
          minHeight: tokens.density.touchMin,
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          cursor: 'pointer',
          fontSize: tokens.font.size.sm,
          boxShadow: tokens.elevation[1],
        }}
      >
        <Icon name="settings" size={20} decorative />
        <span>{activeOption?.label ?? engine}</span>
      </button>
      {open ? (
        <div
          ref={popoverRef}
          role="listbox"
          aria-label="Rendering engine"
          style={{
            position: 'absolute',
            top: `calc(100% + ${tokens.space[2]}px)`,
            left: 0,
            minWidth: tokens.layout.popoverSm,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            boxShadow: tokens.elevation[3],
            padding: tokens.space[2],
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[1],
          }}
        >
          {ENGINE_OPTIONS.map((o) => {
            const isActive = o.id === engine;
            return (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => select(o.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  padding: tokens.space[2],
                  minHeight: tokens.density.touchMin,
                  background: isActive
                    ? tokens.color.accent.subtle
                    : 'transparent',
                  color: tokens.color.ink.primary,
                  border: isActive
                    ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                    : `${tokens.border.width.thin}px solid transparent`,
                  borderRadius: tokens.radius.input,
                  cursor: 'pointer',
                  fontSize: tokens.font.size.sm,
                }}
              >
                <span style={{ fontWeight: tokens.font.weight.medium }}>{o.label}</span>
                <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.secondary }}>
                  {o.description}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
